import {useCallback, useEffect, useMemo, useState} from 'react';
import authContext from 'contexts/authContext';

import api from 'api';

import {User} from 'types';
import {AuthService} from 'services/auth.service';
import toast from 'react-hot-toast';
import {get} from 'lodash';

interface Props {
  children: React.ReactNode;
}

interface RegisterResponse {
  message: string;
  email: string;
  token: string;
}

export interface Permissions {
  [key: string]: {
    permissions: {
      read: boolean;
      write: boolean;
      delete: boolean;
    };
  };
}

function AuthProvider({children}: Props) {
  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [rolePermission, setRolePermission] = useState<Permissions>({});
  const [token, setToken] = useState<string>();
  const [loading, setLoading] = useState(true);

  const componentDidMount = useCallback(async function (first: boolean) {
    try {
      const authService = new AuthService();
      setLoading(first);
      const {data} = await authService.getProfile();
      const permission = data?.user_menu_permissions;
      const obj: Permissions = {};
      permission.forEach((item: any) => {
        obj[item.menu.slug] = item;
      });
      setRolePermission(obj);
      setCurrentUser({...data});
    } catch (error) {
      localStorage.removeItem('token');
      setToken(undefined);
      setCurrentUser(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(
    function () {
      componentDidMount(true);
    },
    [token, componentDidMount]
  );

  const setup = useCallback(async function (token: string) {
    localStorage.setItem('token', token);
    setToken(token);
  }, []);

  const register = useCallback(
    async function (data: any) {
      try {
        const {
          data: {token},
        } = await api.post<RegisterResponse>('/users/register/', {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          password: data.password,
          company: {
            name: data.company_name,
            type: data.company_type,
          },
        });

        setup(token);
      } catch (error) {
        const err = get(error, 'response.data', '');

        let error_message = 'Something went wrong. Please try again later.';
        if (typeof err === 'object') {
          error_message = JSON.stringify(err);
        }
        toast.error(error_message);
        throw Error('Registration error');
      }
    },
    [setup]
  );

  const login = useCallback(
    async function (email: string, password: string) {
      const authService = new AuthService();
      const {
        data: {token},
      } = await authService.login(email, password);
      await setup(token);
    },
    [setup]
  );

  const logout = useCallback(async function () {
    setLoading(true);
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
      sessionStorage.clear();
      setToken(undefined);
      setCurrentUser(undefined);
    } catch (error) {
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const setUser = useCallback((user: User) => {
    setCurrentUser(user);
  }, []);

  const auth = useMemo(
    function () {
      return {
        currentUser,
        token,
        loading,
        register,
        login,
        logout,
        setUser,
        refetch: componentDidMount,
        rolePermission,
      };
    },
    [
      currentUser,
      token,
      loading,
      register,
      login,
      logout,
      setUser,
      componentDidMount,
      rolePermission,
    ]
  );

  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export default AuthProvider;
